/**
 * Eine Figur **außerhalb** des Brettes — als eigenständiges Zeichen im Fließtext.
 *
 * **Warum es das gibt** (Zyklus R3, Befund aus dem Spieltesten): eine Figur, die in den
 * Respawn-Pool wandert, stand bis dahin nur als Wort in einer Liste und fiel kaum auf. Das
 * Symbol ist seit dem 2026-10-05 die sprachunabhängige Auskunft über die Figurenklasse
 * (**E33**) — im Vorrat hat es genauso zu stehen wie auf dem Brett, sonst muss man zweimal
 * hinsehen.
 *
 * **Dieselbe Figur wie auf dem Brett, nicht eine zweite Zeichnung.** Kreis in der Figurenfarbe
 * des Sektors, Kontur, darauf das Symbol in der Symbolfarbe desselben Sektors (**E34**) — alle
 * Werte aus `palette.ts` und die Form aus `symbols.tsx`. Eine eigene kleine Variante wäre eine
 * zweite Wahrheit darüber, wie ein Läufer aussieht, und sie liefe beim nächsten Designwechsel
 * auseinander.
 *
 * **Es trägt keinen Text, und das ist eine Eigenschaft, keine Auslassung.** Dadurch lässt es
 * sich in eine bestehende Zeile einsetzen, ohne deren `textContent` zu verändern — die Zusagen
 * über die Unterzeile des Statusbereichs (drei Sichtprüfungsbefunde aus 10b, 10d und 10e)
 * gelten unverändert weiter. Die Auskunft für Vorlesewerkzeuge steht im Text **daneben**, nicht
 * hier: `aria-hidden`, weil der Name ohnehin folgt und eine doppelte Ansage störte.
 */

import type { SectorId, PieceType } from '@flaghex/engine';

import {
  PIECE_RADIUS_RATIO,
  PIECE_STROKE_COLOR,
  PIECE_STROKE_RATIO,
  PIECE_SYMBOL_RATIO,
  SECTOR_COLORS,
} from './palette.js';
import { pieceSymbol } from './symbols.js';

export interface PieceGlyphProps {
  readonly type: PieceType;
  readonly sector: SectorId;
  /** Kantenlänge in Pixeln. Vorgabe passt in eine Textzeile. */
  readonly size?: number;
}

/**
 * Der Bezugsradius, auf den sich die Verhältnisse aus `palette.ts` beziehen.
 *
 * Auf dem Brett ist das die halbe Feldgröße; hier ist es die halbe Kantenlänge des Zeichens.
 * Damit stimmen die Proportionen Kreis/Symbol/Kontur mit dem Brett überein, ohne dass die
 * Zahlen ein zweites Mal dastehen.
 */
const VIEW = 100;

export function PieceGlyph({ type, sector, size = 18 }: PieceGlyphProps) {
  const colors = SECTOR_COLORS[sector];
  const r = VIEW / 2;

  return (
    <svg
      className="glyph"
      width={size}
      height={size}
      viewBox={`${-r} ${-r} ${VIEW} ${VIEW}`}
      role="presentation"
      aria-hidden="true"
      data-piece-glyph={type}
    >
      <circle
        cx={0}
        cy={0}
        r={r * PIECE_RADIUS_RATIO}
        fill={colors.piece}
        stroke={PIECE_STROKE_COLOR}
        strokeWidth={r * PIECE_STROKE_RATIO}
      />
      {pieceSymbol(type, { r: r * PIECE_SYMBOL_RATIO, color: colors.symbol })}
    </svg>
  );
}
